import { useCallback, useEffect, useState } from 'react';

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Chip } from '@/components/atoms/Chip/Chip';
import Link from '@/components/atoms/Link/Link';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { WhatsAppTemplateCombobox } from '@/components/atoms/WhatsAppTemplateCombobox';
import { WHATSAPP_MANAGER_URL } from '@/modules/whatsapp/constants';
import { getComponentsFromTemplate, getVariablesLength } from '@/modules/whatsapp/utils';
import { FieldsetStatus, Form } from '@/shared/types/broadcast';
import { capitalizeFirstLetter, convertNameToTag } from '@/util/util';

import { TemplateBody } from './TemplateBody';
import { useSubscribers } from '../../hooks/useSubscribers';
import { selectBroadcast, selectSubscribers, selectValidTemplates } from '../../redux/selectors';
import StepFieldset from '../StepFieldset/StepFieldset';

interface Props {
  isReadOnly: boolean;
}

export const MessageFieldset = ({ isReadOnly }: Props) => {
  const { t } = useTranslation();
  const { control, getValues, setValue, clearErrors } = useFormContext<Form>();
  const [stepStatus, setStepStatus] = useState<FieldsetStatus>('disabled');
  const templates = useSelector(selectValidTemplates);
  const broadcast = useSelector(selectBroadcast);
  const { subscribers: APISubscribers } = useSubscribers(broadcast.broadcast_id);

  const { append: headerAppend } = useFieldArray({ control, name: 'headerParameters' });

  const { append: bodyAppend } = useFieldArray({ control, name: 'bodyParameters' });

  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const { name } = broadcast;

  const subscribers = useSelector(selectSubscribers);
  const { value: templateId } = useWatch({ control, name: 'template' }) ?? {};
  const template = templates.find((t) => t.id === templateId);
  const isPreviousStepValid =
    subscribers.length > 0 || APISubscribers?.pages?.[0].subscribers?.length > 0;

  useEffect(() => {
    if (!isPreviousStepValid) {
      setStepStatus('disabled');
    }
    if (isPreviousStepValid && templateId) {
      setStepStatus('completed');
    }
    if (stepStatus !== 'completed' && isPreviousStepValid && !templateId) {
      setStepStatus('active');
    }
  }, [getValues, stepStatus, templateId, isPreviousStepValid]);

  const handleTemplateChange = useCallback(
    (newTemplateId: string) => {
      if (newTemplateId !== templateId) {
        setValue('headerParameters', []);
        setValue('bodyParameters', []);
        setValue('headerMediaParam', null);
        clearErrors('headerParameters');
        clearErrors('bodyParameters');
        const selectedTemplate = templates.find((t) => t.id === newTemplateId);

        if (selectedTemplate) {
          // Update the form value with the full object structure
          setValue('template', {
            value: selectedTemplate.id,
            label: selectedTemplate.name,
            type: selectedTemplate.language,
          });

          const { header, body } = getComponentsFromTemplate(selectedTemplate);
          for (let i = 0; i < getVariablesLength(header); i++) {
            headerAppend({ name: null });
          }
          for (let i = 0; i < getVariablesLength(body); i++) {
            bodyAppend({ name: null });
          }
        } else {
          setValue('template', null);
        }
      }
    },
    [templateId, setValue, clearErrors, templates, headerAppend, bodyAppend]
  );

  return (
    <StepFieldset title={capitalizeFirstLetter(t('common.message'))} step={2} status={stepStatus}>
      <TitleSubtitle
        title={t('broadcasts.templates')}
        size="large"
        subtitle={
          <Trans
            i18nKey="broadcasts.select_template"
            components={{ link1: <Link href={WHATSAPP_MANAGER_URL} external /> }}
          />
        }
      />

      <WhatsAppTemplateCombobox
        templates={templates}
        selectedTemplateId={templateId}
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        disabled={stepStatus === 'disabled' || isReadOnly}
        placeholder={t('brains.select_a_template')}
        // Broadcasts require a template; ignore the deselect (null) path.
        onSelect={(id) => {
          if (id) {
            handleTemplateChange(id);
          }
        }}
      />
      {template && <TemplateBody template={template} key={template.id} isReadOnly={isReadOnly} />}
      <div className="mt-4">
        <TitleSubtitle title={t('rules.tag')} subtitle={t('broadcasts.select_tag')} size="large" />

        <Chip size="sm">{convertNameToTag(name)}</Chip>
      </div>
    </StepFieldset>
  );
};
